import { create } from 'zustand';
import { produce } from 'immer';
import { DiagramState, Node, Edge, SocketDefinition, Layer, NodeMeta, NodeGroup, DiagramType, DiagramPage, PageSettings, SequenceParticipant, SequenceMessage, SequenceFragment, SequenceParticipantGroup, Graph, Flow, CatalogComponent } from '../types';
import { isMarkupType } from '../constants/nodeTypes';
import { placeNodeAboveStackParent } from '../utils/drawStack';
import { connectionUsesFreeformSockets, normalizeOutputToInput, applyMagneticDragPolarity } from '../utils/connectionPolarity';
import { enrollNode, inferCatalogFromDocument, mergeIntoCatalog, syncCatalogAcrossDocument } from '../utils/catalog';
import { getKit, type DiagramKitId } from '../utils/diagramKits';
import { createCatalogActions } from './slices/catalogSlice';
import { generateId } from '../utils';
import { calcNodeHeight, socketY } from '../utils/socketLayout';
import { applyFlowLayout, type FlowLayoutResult } from '../utils/elkLayout';
import {
  getAncestorGroupIds,
  getDescendantNodeIds,
  recalcGroupBounds as recalcGroupBoundsHelper,
} from '../utils/groupBounds';
import { createSequenceActions, type SequenceLayoutPatch } from './slices/sequenceSlice';
import {
  createPageActions,
  DEFAULT_LAYER,
  ROOT_GRAPH_ID,
  makeMainPage as _makeMainPage,
  normalizePage as _normalizePage,
  syncPageFromState as _syncPageFromState,
  withLegacySequenceData as _withLegacySequenceData,
} from './slices/pageSlice';
import { createGraphActions } from './slices/graphSlice';

// ── Diagram persistence ─────────────────────────────────────────────────
const DIAGRAM_STORAGE_KEY = 'velostruct-diagram';
let diagramPersistenceEnabled = true;

export function setDiagramPersistenceEnabled(enabled: boolean) {
  diagramPersistenceEnabled = enabled;
}

type PersistedDiagram = {
  // New subgraph-based model
  graphs?: Record<string, Graph>;
  activeGraphId?: string;
  graphNavStack?: Array<{ graphId: string; graphLabel: string; returnViewport: { x: number; y: number; scale: number } }>;
  
  // Legacy flat state (for backward compat)
  nodes: Node[];
  edges: Edge[];
  viewport: DiagramState['viewport'];
  layers: Layer[];
  activeLayerId: string;
  groups: NodeGroup[];
  diagramType?: DiagramType;
  participants?: SequenceParticipant[];
  messages?: SequenceMessage[];
  fragments?: SequenceFragment[];
  participantGroups?: SequenceParticipantGroup[];
  pages?: DiagramPage[];
  activePage?: string;
  flows?: Flow[];
  seqLifelineLength?: number;
  catalog?: CatalogComponent[];
};

function loadPersistedDiagram(): Partial<PersistedDiagram> {
  try {
    const raw = localStorage.getItem(DIAGRAM_STORAGE_KEY);
    if (!raw) return {};
    return JSON.parse(raw) as PersistedDiagram;
  } catch {
    return {};
  }
}

function savePersistedDiagram(state: DiagramStore) {
  if (!diagramPersistenceEnabled) return;
  try {
    // Sync current graph state before persisting
    const syncedGraphs = { ...state.graphs };
    if (syncedGraphs[state.activeGraphId]) {
      syncedGraphs[state.activeGraphId] = {
        ...syncedGraphs[state.activeGraphId],
        nodes: state.nodes,
        edges: state.edges,
        viewport: state.viewport,
      };
    }

    // Also sync legacy pages for backward compat
    const syncedPages = state.pages.map(page =>
      page.id === state.activePage ? _syncPageFromState(state, page) : page
    );

    localStorage.setItem(DIAGRAM_STORAGE_KEY, JSON.stringify({
      // New subgraph model
      graphs: syncedGraphs,
      activeGraphId: state.activeGraphId,
      graphNavStack: state.graphNavStack,
      
      // Legacy flat state
      nodes: state.nodes,
      edges: state.edges,
      viewport: state.viewport,
      layers: state.layers,
      activeLayerId: state.activeLayerId,
      groups: state.groups,
      diagramType: state.diagramType,
      participants: state.participants,
      messages: state.messages,
      fragments: state.fragments,
      participantGroups: state.participantGroups,
      seqLifelineLength: state.seqLifelineLength,
      pages: syncedPages,
      activePage: state.activePage,
      flows: state.flows,
      catalog: state.catalog,
    } satisfies PersistedDiagram));
  } catch {
    // quota exceeded — ignore silently
  }
}

const emptyConnectionState = {
  isConnecting: false,
  sourceNodeId: null as string | null,
  sourceSocketId: null as string | null,
  tempEdge: null as { startX: number; startY: number; endX: number; endY: number } | null,
};

interface DiagramStore extends DiagramState {
  connectionState: {
    isConnecting: boolean;
    sourceNodeId: string | null;
    sourceSocketId: string | null;
    tempEdge: { startX: number; startY: number; endX: number; endY: number } | null;
  };

  // ── Subgraph-based grouping ─────────────────────────────────────────────
  // Each graph is a self-contained node/edge collection. The root graph is
  // the main canvas. Group nodes reference child graphs by childGraphId.
  graphs: Record<string, Graph>;
  activeGraphId: string;  // ID of the currently displayed graph

  // Navigation stack for entering/exiting groups
  graphNavStack: Array<{
    graphId: string;
    graphLabel: string;
    returnViewport: { x: number; y: number; scale: number };
  }>;

  // Frames (visual-only grouping, kept separate from functional groups)
  frames: NodeGroup[];

  // Legacy groups field (kept for backward compat during migration)
  groups: NodeGroup[];

  // Flows — named selections of nodes+edges for hold-to-preview highlighting
  flows: Flow[];

  // Multi-page workspace
  pages: DiagramPage[];
  activePage: string;  // ID of the currently displayed root page

  // Architecture catalog — identity reused across pages and sequence
  catalog: CatalogComponent[];

  // Legacy group navigation stack (deprecated, use graphNavStack)
  groupNavStack: Array<{
    groupId: string;
    groupLabel: string;
    returnViewport: { x: number; y: number; scale: number };
  }>;

  // Node CRUD
  addNode: (node: Omit<Node, 'id'>) => void;
  updateNode: (id: string, updates: Partial<Node>) => void;
  moveStackAboveParent: (nodeId: string, parentId: string) => void;
  removeNode: (id: string) => void;
  /**
   * Move the active graph onto a finished automatic layout: node positions,
   * frame bounds, socket placement and one route per edge, all decided in the
   * same run.
   */
  applyLayout: (layout: FlowLayoutResult) => void;

  // Edge CRUD
  addEdge: (edge: Omit<Edge, 'id'>) => void;
  removeEdge: (id: string) => void;
  updateEdge: (id: string, updates: Partial<Edge>) => void;

  // Selection
  selectItem: (id: string, multiSelect?: boolean) => void;
  clearSelection: () => void;

  // Viewport
  updateViewport: (viewport: Partial<DiagramState['viewport']>) => void;

  // Persistence
  loadDiagram: (data: { diagramType?: DiagramType; participants?: SequenceParticipant[]; messages?: SequenceMessage[]; fragments?: SequenceFragment[]; participantGroups?: SequenceParticipantGroup[]; seqLifelineLength?: number; nodes: Node[]; edges: Edge[]; viewport: DiagramState['viewport']; layers?: Layer[]; groups?: NodeGroup[]; graphs?: Record<string, Graph>; activeGraphId?: string; pages?: DiagramPage[]; activePage?: string; catalog?: CatalogComponent[] }) => void;
  loadRawData: (diagramType: DiagramType, participants: SequenceParticipant[], messages: SequenceMessage[], fragments: SequenceFragment[], nodes: Node[], edges: Edge[], groups: NodeGroup[], participantGroups?: SequenceParticipantGroup[], layers?: Layer[], flows?: Flow[]) => void;
  clearDiagram: (diagramType?: DiagramType) => void;
  replaceActivePageFlow: (data: { nodes: Node[]; edges: Edge[]; groups?: NodeGroup[] }) => void;

  // Sockets
  addSocket: (nodeId: string, direction: 'input' | 'output', name?: string) => string | null;
  removeSocket: (nodeId: string, socketId: string) => void;
  renameSocket: (nodeId: string, socketId: string, newLabel: string) => void;
  reorderSocket: (nodeId: string, socketId: string, dir: 'up' | 'down') => void;
  moveSocket: (nodeId: string, socketId: string, toIndex: number) => void;

  // Layers
  addLayer: (name: string, color: string) => void;
  removeLayer: (id: string) => void;
  updateLayer: (id: string, updates: Partial<Layer>) => void;
  setNodeLayer: (nodeId: string, layerId: string | undefined) => void;
  setParticipantLayer: (participantId: string, layerId: string | undefined) => void;
  setActiveLayer: (id: string) => void;

  // Node metadata
  updateNodeMeta: (nodeId: string, meta: Partial<NodeMeta>) => void;
  updateCatalog: (catalogId: string, patch: Partial<CatalogComponent>) => void;
  placeCatalogInstance: (catalogId: string, position?: { x: number; y: number }) => string | undefined;
  createChildView: (nodeId: string, options?: { name?: string; diagramType?: DiagramType }) => string | undefined;
  createC4Workspace: (systemName?: string) => { contextPageId: string; containersPageId: string; sequencePageId: string };

  // Flows
  addFlow: (name: string, nodeIds: string[], edgeIds: string[]) => void;
  removeFlow: (id: string) => void;
  renameFlow: (id: string, name: string) => void;
  updateFlow: (id: string, nodeIds: string[], edgeIds: string[]) => void;

  // ── Subgraph-based grouping ───────────────────────────────────────────
  // Create a group from selected nodes — moves them to a new child graph
  createGroup: (nodeIds: string[], label?: string) => string | undefined;
  // Create an empty group node that can be entered and populated
  createEmptyGroup: (position: { x: number; y: number }, label?: string) => string;
  // Dissolve a group — moves child graph contents back to parent graph
  dissolveGroup: (groupNodeId: string) => void;
  // Navigate into a group's child graph
  enterGraph: (groupNodeId: string) => void;
  // Navigate back to parent graph
  exitGraph: () => void;
  // Get the currently active graph
  getActiveGraph: () => Graph | undefined;

  // ── Frames (visual-only grouping) ────────────────────────────────────────
  createFrame: (position: { x: number; y: number }, size: { width: number; height: number }, nodeIds: string[], groupIds?: string[]) => string;
  updateFrame: (frameId: string, updates: Partial<NodeGroup>) => void;
  deleteFrame: (frameId: string) => void;

  // Legacy group actions (deprecated — kept for migration)
  groupNodes: (nodeIds: string[], label?: string) => void;
  ungroupNodes: (groupId: string) => void;
  toggleGroupCollapse: (groupId: string) => void;
  updateGroup: (groupId: string, updates: Partial<NodeGroup>) => void;
  recalcGroupBounds: (groupId: string) => void;
  addNodeToGroup: (nodeId: string, groupId: string) => void;
  removeNodeFromGroup: (nodeId: string, groupId: string) => void;
  addGroupToFrame: (childGroupId: string, frameId: string) => void;
  removeGroupFromFrame: (childGroupId: string, frameId: string) => void;
  enterGroup: (groupId: string) => void;
  exitGroup: () => void;

  // Multi-page actions
  addPage: (name?: string, diagramType?: DiagramType, kit?: DiagramKitId) => string;
  switchPage: (pageId: string) => void;
  setPageDiagramType: (pageId: string, diagramType: DiagramType) => void;
  setPageKit: (pageId: string, kit: DiagramKitId) => void;
  renamePage: (pageId: string, name: string) => void;
  setPageDescription: (pageId: string, description: string) => void;
  setPageSettings: (pageId: string, patch: Partial<PageSettings>) => void;
  deletePage: (pageId: string) => void;
  linkNodeToPage: (nodeId: string, pageId: string) => void;
  unlinkNodePage: (nodeId: string) => void;

  // Connection workflow
  startConnection: (nodeId: string, socketId: string) => void;
  updateTempEdge: (endX: number, endY: number) => void;
  /** Completes a connection and returns the newly created Edge if successful */
  completeConnection: (targetNodeId: string, targetSocketId: string) => Edge | undefined;
  cancelConnection: () => void;

  // Helpers
  getConnectedSockets: () => Set<string>;
  canConnect: (sourceNodeId: string, sourceSocketId: string, targetNodeId: string, targetSocketId: string) => boolean;
  /** Get all descendant node IDs of a group (recursively through sub-groups) */
  getGroupDescendantNodeIds: (groupId: string) => string[];
  /** Get the active page object */
  getActivePage: () => DiagramPage | undefined;

  // Sequence Modeling
  seqLifelineLength: number;
  setSeqLifelineLength: (length: number) => void;
  setDiagramType: (type: DiagramType) => void;
  addParticipant: (participant: Pick<SequenceParticipant, 'label'> & Partial<Omit<SequenceParticipant, 'id' | 'order' | 'label'>>) => void;
  updateParticipant: (id: string, updates: Partial<SequenceParticipant>) => void;
  removeParticipant: (id: string) => void;
  reorderParticipants: () => void;

  addMessage: (message: Omit<SequenceMessage, 'id' | 'y'> & { y?: number }) => void;
  updateMessage: (id: string, updates: Partial<SequenceMessage>) => void;
  applySequenceLayoutPatch: (patch: SequenceLayoutPatch) => void;
  removeMessage: (id: string) => void;

  addFragment: (fragment: Omit<SequenceFragment, 'id'>) => void;
  updateFragment: (id: string, updates: Partial<SequenceFragment>) => void;
  removeFragment: (id: string) => void;
  /** Move a fragment (and all nested children + their messages) by deltaY; auto-detects new parent. */
  moveFragment: (id: string, deltaY: number) => void;

  // Sequence Participant Groups
  participantGroups: SequenceParticipantGroup[];
  groupParticipants: (participantIds: string[], label?: string) => void;
  ungroupParticipants: (groupId: string) => void;
  toggleParticipantGroupCollapse: (groupId: string) => void;
  updateParticipantGroup: (id: string, updates: Partial<SequenceParticipantGroup>) => void;
  replaceSequenceContent: (data: {
    participants: SequenceParticipant[];
    messages: SequenceMessage[];
    fragments: SequenceFragment[];
    participantGroups?: SequenceParticipantGroup[];
    seqLifelineLength?: number;
  }) => void;
}

// ── Helper: recalculate group bounding box from children ────────────────
function _recalcBounds(state: DiagramStore, groupId: string) {
  recalcGroupBoundsHelper(state.groups, state.nodes, groupId);
}

const _getDescendantNodeIds = getDescendantNodeIds;
const _getAncestorGroupIds = getAncestorGroupIds;

const _persisted = loadPersistedDiagram();

// If persisted data has no pages array, migrate v1 flat data into pages[0]
const _initialPagesBase: DiagramPage[] = _persisted.pages?.length
  ? _persisted.pages.map(_normalizePage)
  : [_makeMainPage(_persisted)];
const _initialActivePage = _persisted.activePage ?? _initialPagesBase[0]?.id ?? 'main';
const _initialPages = _withLegacySequenceData(_initialPagesBase, _persisted, _initialActivePage);
const _activePg = _initialPages.find(p => p.id === _initialActivePage) ?? _initialPages[0];

// Use persisted graphs if available, otherwise create root graph from legacy flat state
const _initialGraphs: Record<string, Graph> = _persisted.graphs && Object.keys(_persisted.graphs).length > 0
  ? _persisted.graphs
  : {
      [ROOT_GRAPH_ID]: {
        id: ROOT_GRAPH_ID,
        nodes: _activePg?.nodes ?? [],
        edges: _activePg?.edges ?? [],
        viewport: _activePg?.viewport ?? { x: 0, y: 0, scale: 1 },
      },
    };

const _initialActiveGraphId = _persisted.activeGraphId ?? ROOT_GRAPH_ID;
const _activeGraph = _initialGraphs[_initialActiveGraphId] ?? _initialGraphs[ROOT_GRAPH_ID];

const _initialCatalog: CatalogComponent[] = inferCatalogFromDocument({
  catalog: _persisted.catalog ?? [],
  nodes: _activeGraph?.nodes ?? [],
  participants: _activePg?.participants ?? _persisted.participants ?? [],
  pages: _initialPages,
  graphs: _initialGraphs,
});

export const useDiagramStore = create<DiagramStore>((set, get) => ({
  // ── Subgraph-based state (new model) ────────────────────────────────────
  graphs: _initialGraphs,
  activeGraphId: _initialActiveGraphId,
  graphNavStack: _persisted.graphNavStack ?? [],
  frames: [],
  flows: (_persisted.flows ?? []).map((f: Flow) => ({ id: f.id, name: f.name, nodeIds: f.nodeIds ?? [], edgeIds: f.edgeIds ?? [] })),

  // ── Flat state (derived from active graph for compatibility) ────────────
  nodes: _activeGraph?.nodes ?? [],
  edges: _activeGraph?.edges ?? [],
  selectedItems: [],
  viewport: _activeGraph?.viewport ?? { x: 0, y: 0, scale: 1 },
  layers: _activePg?.layers?.length ? _activePg.layers : [{ ...DEFAULT_LAYER }],
  activeLayerId: _persisted.activeLayerId ?? 'default',
  connectionState: { ...emptyConnectionState },

  // ── Legacy fields (kept for backward compat) ────────────────────────────
  groups: _activePg?.groups ?? [],
  pages: _initialPages,
  activePage: _initialActivePage,
  catalog: _initialCatalog,
  groupNavStack: [],

  // ── Sequence diagram state ──────────────────────────────────────────────
  diagramType: _activePg?.diagramType ?? _persisted.diagramType ?? 'flow',
  seqLifelineLength: _activePg?.seqLifelineLength ?? _persisted.seqLifelineLength ?? 500,
  participants: _activePg?.participants ?? _persisted.participants ?? [],
  messages: _activePg?.messages ?? _persisted.messages ?? [],
  fragments: _activePg?.fragments ?? _persisted.fragments ?? [],
  participantGroups: _activePg?.participantGroups ?? _persisted.participantGroups ?? [],

  addNode: (nodeData) =>
    set(produce((state: DiagramStore) => {
      const layerId = nodeData.layerId ?? state.activeLayerId;
      const newNode: Node = { ...nodeData, id: generateId('node'), layerId: layerId === 'default' ? undefined : layerId };
      enrollNode(state, newNode);
      const graph = state.graphs[state.activeGraphId];
      if (graph) graph.nodes.push(newNode);
      state.nodes = graph?.nodes ?? state.nodes;
    })),

  updateNode: (id, updates) =>
    set(produce((state: DiagramStore) => {
      const graph = state.graphs[state.activeGraphId];
      if (graph) {
        const node = graph.nodes.find((n: Node) => n.id === id);
        if (node) {
          Object.assign(node, updates);
          if (updates.label !== undefined || updates.meta !== undefined) {
            enrollNode(state, node);
            if (node.catalogId) {
              const entry = state.catalog.find(item => item.id === node.catalogId);
              if (entry) {
                if (updates.label !== undefined) mergeIntoCatalog(entry, { name: node.label });
                if (updates.meta) {
                  mergeIntoCatalog(entry, {
                    description: node.meta?.description,
                    owner: node.meta?.owner,
                    status: node.meta?.status,
                    links: node.meta?.links,
                  });
                }
                syncCatalogAcrossDocument(state, node.catalogId);
              }
            }
          }
        }
        state.nodes = graph.nodes;
      }
    })),

  moveStackAboveParent: (nodeId, parentId) =>
    set(produce((state: DiagramStore) => {
      const graph = state.graphs[state.activeGraphId];
      if (!graph) return;
      graph.nodes = placeNodeAboveStackParent(graph.nodes, nodeId, parentId);
      state.nodes = graph.nodes;
    })),

  applyLayout: (layout) =>
    set(produce((state: DiagramStore) => {
      const graph = state.graphs[state.activeGraphId];
      if (!graph) return;
      applyFlowLayout(graph.nodes, graph.edges, state.groups, layout);
      state.nodes = graph.nodes;
      state.edges = graph.edges;
    })),

  replaceActivePageFlow: (data) =>
    set(produce((state: DiagramStore) => {
      const viewport = { x: 0, y: 0, scale: 1 };
      const nodes = data.nodes.map((node) => ({ ...node }));
      const edges = data.edges.map((edge) => ({ ...edge }));
      const groups = (data.groups ?? []).map((group) => ({
        ...group,
        childGroupIds: [...(group.childGroupIds ?? [])],
      }));
      const graphs = {
        [ROOT_GRAPH_ID]: { id: ROOT_GRAPH_ID, nodes, edges, viewport },
      };
      state.diagramType = 'flow';
      state.nodes = nodes;
      state.edges = edges;
      state.groups = groups;
      state.viewport = viewport;
      state.graphs = graphs;
      state.activeGraphId = ROOT_GRAPH_ID;
      state.selectedItems = [];
      state.connectionState = { ...emptyConnectionState };
      state.graphNavStack = [];
      state.groupNavStack = [];
      const page = state.pages.find((entry) => entry.id === state.activePage);
      if (page) {
        page.diagramType = 'flow';
        page.nodes = nodes;
        page.edges = edges;
        page.groups = groups;
        page.viewport = viewport;
        page.graphs = graphs;
      }
      state.catalog = inferCatalogFromDocument(state);
    })),

  removeNode: (id) =>
    set(produce((state: DiagramStore) => {
      const graph = state.graphs[state.activeGraphId];
      if (graph) {
        // If removing a group node, also delete its child graph
        const node = graph.nodes.find((n: Node) => n.id === id);
        if (node?.childGraphId) {
          delete state.graphs[node.childGraphId];
        }
        graph.nodes = graph.nodes.filter((n: Node) => n.id !== id);
        graph.edges = graph.edges.filter((e: Edge) => e.sourceNodeId !== id && e.targetNodeId !== id);
        state.nodes = graph.nodes;
        state.edges = graph.edges;
      }
      state.selectedItems = state.selectedItems.filter((item: string) => item !== id);
      // Remove from frames
      state.frames.forEach((f: NodeGroup) => {
        f.childNodeIds = f.childNodeIds.filter((cid: string) => cid !== id);
      });
    })),

  addEdge: (edgeData) =>
    set(produce((state: DiagramStore) => {
      const graph = state.graphs[state.activeGraphId];
      if (graph) {
        graph.edges.push({ ...edgeData, id: generateId('edge') });
        state.edges = graph.edges;
      }
    })),

  removeEdge: (id) =>
    set(produce((state: DiagramStore) => {
      const graph = state.graphs[state.activeGraphId];
      if (graph) {
        graph.edges = graph.edges.filter((e: Edge) => e.id !== id);
        state.edges = graph.edges;
      }
      state.selectedItems = state.selectedItems.filter((item: string) => item !== id);
    })),

  updateEdge: (id, updates) =>
    set(produce((state: DiagramStore) => {
      const graph = state.graphs[state.activeGraphId];
      if (graph) {
        const edge = graph.edges.find((e: Edge) => e.id === id);
        if (edge) Object.assign(edge, updates);
        state.edges = graph.edges;
      }
    })),

  selectItem: (id, multiSelect = false) =>
    set(produce((state: DiagramStore) => {
      if (!multiSelect) {
        // Single-select mode: replace entire selection
        state.selectedItems = [id];
      } else {
        // Multi-select mode: toggle add/remove
        if (state.selectedItems.includes(id)) {
          state.selectedItems = state.selectedItems.filter(item => item !== id);
        } else {
          state.selectedItems.push(id);
        }
      }
    })),

  clearSelection: () =>
    set(produce((state: DiagramStore) => { state.selectedItems = []; })),

  updateViewport: (viewport) =>
    set(produce((state: DiagramStore) => { Object.assign(state.viewport, viewport); })),

  loadDiagram: (data) =>
    set(produce((state: DiagramStore) => {
      state.nodes = data.nodes;
      state.edges = data.edges;
      state.viewport = data.viewport;
      state.layers = (data.layers && data.layers.length > 0) ? data.layers : [{ ...DEFAULT_LAYER }];
      state.groups = (data.groups ?? []).map((g: NodeGroup) => ({
        ...g,
        childGroupIds: g.childGroupIds ?? [],
      }));
      // Restore full graphs and pages if provided (avoids losing subgraph edges)
      if (data.graphs && Object.keys(data.graphs).length > 0) {
        state.graphs = data.graphs;
        state.activeGraphId = data.activeGraphId ?? ROOT_GRAPH_ID;
      } else {
        // Rebuild root graph from flat state
        state.graphs = {
          [ROOT_GRAPH_ID]: { id: ROOT_GRAPH_ID, nodes: data.nodes, edges: data.edges, viewport: data.viewport },
        };
        state.activeGraphId = ROOT_GRAPH_ID;
      }
      if (data.pages && data.pages.length > 0) {
        state.pages = data.pages.map(_normalizePage);
        state.activePage = data.activePage ?? data.pages[0].id;
        const activePage = state.pages.find(page => page.id === state.activePage) ?? state.pages[0];
        state.diagramType = activePage.diagramType ?? data.diagramType ?? 'flow';
        state.participants = activePage.participants ?? data.participants ?? [];
        state.messages = activePage.messages ?? data.messages ?? [];
        state.fragments = activePage.fragments ?? data.fragments ?? [];
        state.participantGroups = activePage.participantGroups ?? data.participantGroups ?? [];
        state.seqLifelineLength = activePage.seqLifelineLength ?? data.seqLifelineLength ?? 500;
      } else {
        const pg = state.pages.find(p => p.id === state.activePage);
        if (pg) {
          pg.nodes = state.nodes;
          pg.edges = state.edges;
          pg.groups = state.groups;
          pg.layers = state.layers;
          pg.viewport = state.viewport;
          pg.diagramType = data.diagramType ?? pg.diagramType ?? 'flow';
          pg.participants = data.participants ?? [];
          pg.messages = data.messages ?? [];
          pg.fragments = data.fragments ?? [];
          pg.participantGroups = data.participantGroups ?? [];
          pg.seqLifelineLength = data.seqLifelineLength ?? 500;
        }
        state.diagramType = data.diagramType ?? 'flow';
        state.participants = data.participants ?? [];
        state.messages = data.messages ?? [];
        state.fragments = data.fragments ?? [];
        state.participantGroups = data.participantGroups ?? [];
        state.seqLifelineLength = data.seqLifelineLength ?? 500;
      }
      state.selectedItems = [];
      state.connectionState = { ...emptyConnectionState };
      state.groupNavStack = [];
      state.graphNavStack = [];
      if (data.catalog && data.catalog.length > 0) {
        state.catalog = data.catalog.map(entry => ({ ...entry, links: entry.links?.map(link => ({ ...link })) }));
      } else {
        state.catalog = inferCatalogFromDocument(state);
      }
    })),

  loadRawData: (diagramType, participants, messages, fragments, nodes, edges, groups, participantGroups, layers, flows) =>
    set(produce((state: DiagramStore) => {
      state.diagramType = diagramType;
      state.participants = participants;
      state.messages = messages;
      state.fragments = fragments;
      state.participantGroups = participantGroups ?? [];
      state.nodes = nodes;
      state.edges = edges;
      state.groups = groups.map(g => ({
        ...g,
        childGroupIds: g.childGroupIds ?? [],
      }));
      if (layers && layers.length > 0) state.layers = layers;
      if (flows) state.flows = flows;
      state.selectedItems = [];
      state.connectionState = { ...emptyConnectionState };
      state.groupNavStack = [];
    })),

  clearDiagram: (diagramType = 'flow') =>
    set(produce((state: DiagramStore) => {
      state.nodes = [];
      state.edges = [];
      state.selectedItems = [];
      state.layers = [{ ...DEFAULT_LAYER }];
      state.activeLayerId = 'default';
      state.groups = [];
      state.connectionState = { ...emptyConnectionState };
      state.groupNavStack = [];
      state.graphNavStack = [];
      state.activeGraphId = ROOT_GRAPH_ID;
      state.graphs = {
        [ROOT_GRAPH_ID]: { id: ROOT_GRAPH_ID, nodes: [], edges: [], viewport: { x: 0, y: 0, scale: 1 } },
      };
      // Reset to a single blank page
      const blankPage: DiagramPage = {
        id: 'main',
        name: 'Main',
        diagramType,
        nodes: [],
        edges: [],
        groups: [],
        layers: [{ ...DEFAULT_LAYER }],
        viewport: { x: 0, y: 0, scale: 1 },
        participants: [],
        messages: [],
        fragments: [],
        participantGroups: [],
        seqLifelineLength: state.seqLifelineLength ?? 500,
        graphs: {
          [ROOT_GRAPH_ID]: { id: ROOT_GRAPH_ID, nodes: [], edges: [], viewport: { x: 0, y: 0, scale: 1 } },
        },
      };
      state.pages = [blankPage];
      state.activePage = 'main';
      state.diagramType = diagramType;
      state.participants = [];
      state.messages = [];
      state.fragments = [];
      state.participantGroups = [];
      state.flows = [];
      state.catalog = [];
    })),

  addSocket: (nodeId, direction, name) => {
    let newSocketId: string | null = null;
    set(produce((state: DiagramStore) => {
      // Find node in active graph
      const graph = state.graphs[state.activeGraphId];
      const node = graph?.nodes.find((n: Node) => n.id === nodeId) ?? state.nodes.find(n => n.id === nodeId);
      if (!node || isMarkupType(node.type)) return;
      const sameType = node.sockets.filter(s => s.type === direction);
      const newIndex = sameType.length;
      const label = name ?? (direction === 'input' ? `Input ${newIndex + 1}` : `Output ${newIndex + 1}`);
      const socketId = `${direction === 'input' ? 'in' : 'out'}-${newIndex}`;
      newSocketId = socketId;
      node.sockets.push({
        id: socketId,
        type: direction,
        label,
        dataType: 'data',
        position: { x: direction === 'input' ? 0 : node.size.width, y: socketY(newIndex) },
        order: newIndex
      });
      const inCount = node.sockets.filter(s => s.type === 'input').length;
      const outCount = node.sockets.filter(s => s.type === 'output').length;
      const epCount = (node.type === 'api' && Array.isArray(node.data?.endpoints)) ? node.data.endpoints.length : 0;
      node.size.height = calcNodeHeight(inCount, outCount, epCount);

      // ── Mirror socket between boundary node and parent group node ──────────
      // When adding a socket to group_input or group_output, mirror it to the
      // parent group node so both stay in sync. Convention:
      //   group_input  output socket (out-N) ↔ group node input socket (gi-N)
      //   group_output input socket  (in-N)  ↔ group node output socket (go-N)
      if (node.type === 'group_input' || node.type === 'group_output') {
        // Find the parent graph's group node that owns this child graph
        const currentGraphId = state.activeGraphId;
        for (const gid of Object.keys(state.graphs)) {
          const parentGraph = state.graphs[gid];
          const parentGroupNode = parentGraph.nodes.find((n: Node) => n.childGraphId === currentGraphId);
          if (!parentGroupNode) continue;

          if (node.type === 'group_input' && direction === 'output') {
            // New output socket on GroupInput → new input socket on group node
            const mirrorIdx = parentGroupNode.sockets.filter((s: SocketDefinition) => s.type === 'input').length;
            const mirrorId = `gi-${mirrorIdx}`;
            parentGroupNode.sockets.push({
              id: mirrorId,
              type: 'input',
              label,
              dataType: 'data',
              position: { x: 0, y: socketY(mirrorIdx) },
              order: mirrorIdx,
            });
            const pgIn = parentGroupNode.sockets.filter((s: SocketDefinition) => s.type === 'input').length;
            const pgOut = parentGroupNode.sockets.filter((s: SocketDefinition) => s.type === 'output').length;
            parentGroupNode.size.height = calcNodeHeight(pgIn, pgOut);
            // Sync parent graph flat state if it is the root/active flat state
            if (gid === ROOT_GRAPH_ID || parentGraph === graph) {
              state.nodes = [...parentGraph.nodes];
            }
          } else if (node.type === 'group_output' && direction === 'input') {
            // New input socket on GroupOutput → new output socket on group node
            const mirrorIdx = parentGroupNode.sockets.filter((s: SocketDefinition) => s.type === 'output').length;
            const mirrorId = `go-${mirrorIdx}`;
            parentGroupNode.sockets.push({
              id: mirrorId,
              type: 'output',
              label,
              dataType: 'data',
              position: { x: parentGroupNode.size.width, y: socketY(mirrorIdx) },
              order: mirrorIdx,
            });
            const pgIn = parentGroupNode.sockets.filter((s: SocketDefinition) => s.type === 'input').length;
            const pgOut = parentGroupNode.sockets.filter((s: SocketDefinition) => s.type === 'output').length;
            parentGroupNode.size.height = calcNodeHeight(pgIn, pgOut);
            if (gid === ROOT_GRAPH_ID || parentGraph === graph) {
              state.nodes = [...parentGraph.nodes];
            }
          }
          break;
        }
      }

      // ── Mirror socket from group node to boundary node ─────────────────────
      // When adding input/output to a group node directly, also create the
      // corresponding socket on the boundary node inside the child graph.
      if (node.type === 'group' && node.childGraphId) {
        const childGraph = state.graphs[node.childGraphId];
        if (childGraph) {
          if (direction === 'input') {
            // New input on group node → new output socket on GroupInput boundary node
            const giNode = childGraph.nodes.find((n: Node) => n.type === 'group_input');
            if (giNode) {
              const mirrorIdx = giNode.sockets.filter((s: SocketDefinition) => s.type === 'output').length;
              giNode.sockets.push({
                id: `out-${mirrorIdx}`,
                type: 'output',
                label,
                dataType: 'data',
                position: { x: giNode.size.width, y: socketY(mirrorIdx) },
                order: mirrorIdx,
              });
              const giIn = giNode.sockets.filter((s: SocketDefinition) => s.type === 'input').length;
              const giOut = giNode.sockets.filter((s: SocketDefinition) => s.type === 'output').length;
              giNode.size.height = calcNodeHeight(giIn, giOut);
            } else {
              // No GroupInput yet — create one with this socket
              const giId = generateId('gi');
              const giNode: Node = {
                id: giId,
                type: 'group_input',
                label: 'Group Input',
                position: { x: 50, y: 200 },
                size: { width: 140, height: calcNodeHeight(0, 1) },
                sockets: [{
                  id: 'out-0',
                  type: 'output',
                  label,
                  dataType: 'data',
                  position: { x: 140, y: socketY(0) },
                  order: 0,
                }],
              };
              childGraph.nodes.push(giNode);
            }
          } else {
            // New output on group node → new input socket on GroupOutput boundary node
            const goNode = childGraph.nodes.find((n: Node) => n.type === 'group_output');
            if (goNode) {
              const mirrorIdx = goNode.sockets.filter((s: SocketDefinition) => s.type === 'input').length;
              goNode.sockets.push({
                id: `in-${mirrorIdx}`,
                type: 'input',
                label,
                dataType: 'data',
                position: { x: 0, y: socketY(mirrorIdx) },
                order: mirrorIdx,
              });
              const goIn = goNode.sockets.filter((s: SocketDefinition) => s.type === 'input').length;
              const goOut = goNode.sockets.filter((s: SocketDefinition) => s.type === 'output').length;
              goNode.size.height = calcNodeHeight(goIn, goOut);
            } else {
              // No GroupOutput yet — create one with this socket
              const goId = generateId('go');
              const goNode: Node = {
                id: goId,
                type: 'group_output',
                label: 'Group Output',
                position: { x: 500, y: 200 },
                size: { width: 140, height: calcNodeHeight(1, 0) },
                sockets: [{
                  id: 'in-0',
                  type: 'input',
                  label,
                  dataType: 'data',
                  position: { x: 0, y: socketY(0) },
                  order: 0,
                }],
              };
              childGraph.nodes.push(goNode);
            }
          }
        }
      }

      // Sync to flat state
      if (graph) state.nodes = [...graph.nodes];
    }));
    return newSocketId;
  },

  removeSocket: (nodeId, socketId) =>
    set(produce((state: DiagramStore) => {
      const node = state.nodes.find(n => n.id === nodeId);
      if (!node) return;
      node.sockets = node.sockets.filter(s => s.id !== socketId);
      state.edges = state.edges.filter(e =>
        !(e.sourceNodeId === nodeId && e.sourceSocketId === socketId) &&
        !(e.targetNodeId === nodeId && e.targetSocketId === socketId)
      );
    })),

  reorderSocket: (nodeId, socketId, dir) =>
    set(produce((state: DiagramStore) => {
      const graph = state.graphs[state.activeGraphId];
      const node = graph?.nodes.find((n: Node) => n.id === nodeId) ?? state.nodes.find(n => n.id === nodeId);
      if (!node) return;
      const socket = node.sockets.find(s => s.id === socketId);
      if (!socket) return;
      // Work only within the same type group
      const group = node.sockets.filter(s => s.type === socket.type);
      const idx = group.findIndex(s => s.id === socketId);
      const swapIdx = dir === 'up' ? idx - 1 : idx + 1;
      if (swapIdx < 0 || swapIdx >= group.length) return;
      // Swap orders
      const tempOrder = group[idx].order ?? idx;
      group[idx].order = group[swapIdx].order ?? swapIdx;
      group[swapIdx].order = tempOrder;
      // Recalculate Y positions for all sockets of this type
      const sorted = [...group].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
      sorted.forEach((s, i) => {
        s.position = { x: s.position.x, y: socketY(i) };
        s.order = i;
      });
      if (graph) state.nodes = [...graph.nodes];
    })),

  moveSocket: (nodeId, socketId, toIndex) =>
    set(produce((state: DiagramStore) => {
      const graph = state.graphs[state.activeGraphId];
      const node = graph?.nodes.find((n: Node) => n.id === nodeId) ?? state.nodes.find(n => n.id === nodeId);
      if (!node) return;
      const socket = node.sockets.find(s => s.id === socketId);
      if (!socket) return;
      const group = node.sockets.filter(s => s.type === socket.type).sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
      const fromIndex = group.findIndex(s => s.id === socketId);
      if (fromIndex === -1 || toIndex === fromIndex) return;
      const reordered = [...group];
      const [moved] = reordered.splice(fromIndex, 1);
      reordered.splice(toIndex, 0, moved);
      reordered.forEach((s, i) => { s.order = i; s.position = { x: s.position.x, y: socketY(i) }; });
      if (graph) state.nodes = [...graph.nodes];
    })),

  renameSocket: (nodeId, socketId, newLabel) =>
    set(produce((state: DiagramStore) => {
      // Find node in active graph or flat state
      const graph = state.graphs[state.activeGraphId];
      const node = graph?.nodes.find((n: Node) => n.id === nodeId) ?? state.nodes.find(n => n.id === nodeId);
      if (!node) return;

      // Rename the socket on this node
      const socket = node.sockets.find(s => s.id === socketId);
      if (!socket) return;
      socket.label = newLabel;

      // Sync socket names between group node and boundary nodes
      if (node.type === 'group' && node.childGraphId) {
        // This is a group node - sync to boundary nodes in child graph
        const childGraph = state.graphs[node.childGraphId];
        if (childGraph) {
          // Find corresponding boundary node and socket
          // Group input sockets (gi-N) → GroupInput node output sockets (out-N)
          // Group output sockets (go-N) → GroupOutput node input sockets (in-N)
          if (socketId.startsWith('gi-')) {
            const idx = socketId.replace('gi-', '');
            const giNode = childGraph.nodes.find((n: Node) => n.type === 'group_input');
            if (giNode) {
              const giSocket = giNode.sockets.find(s => s.id === `out-${idx}`);
              if (giSocket) giSocket.label = newLabel;
            }
          } else if (socketId.startsWith('go-')) {
            const idx = socketId.replace('go-', '');
            const goNode = childGraph.nodes.find((n: Node) => n.type === 'group_output');
            if (goNode) {
              const goSocket = goNode.sockets.find(s => s.id === `in-${idx}`);
              if (goSocket) goSocket.label = newLabel;
            }
          }
        }
      } else if (node.type === 'group_input' || node.type === 'group_output') {
        // This is a boundary node - sync to parent group node
        // Find the parent group node by looking for a group node whose childGraphId matches current graph
        const currentGraphId = state.activeGraphId;
        // Search all graphs for a group node that points to this graph
        for (const gid of Object.keys(state.graphs)) {
          const g = state.graphs[gid];
          const parentGroupNode = g.nodes.find((n: Node) => n.childGraphId === currentGraphId);
          if (parentGroupNode) {
            // Found the parent group node
            if (node.type === 'group_input' && socketId.startsWith('out-')) {
              // GroupInput output socket → Group node input socket
              const idx = socketId.replace('out-', '');
              const groupSocket = parentGroupNode.sockets.find(s => s.id === `gi-${idx}`);
              if (groupSocket) groupSocket.label = newLabel;
            } else if (node.type === 'group_output' && socketId.startsWith('in-')) {
              // GroupOutput input socket → Group node output socket
              const idx = socketId.replace('in-', '');
              const groupSocket = parentGroupNode.sockets.find(s => s.id === `go-${idx}`);
              if (groupSocket) groupSocket.label = newLabel;
            }
            break;
          }
        }
      }

      // Sync to flat state
      if (graph) state.nodes = [...graph.nodes];
    })),

  addFlow: (name, nodeIds, edgeIds) =>
    set(produce((state: DiagramStore) => {
      state.flows.push({ id: generateId('flow'), name, nodeIds, edgeIds });
    })),

  removeFlow: (id) =>
    set(produce((state: DiagramStore) => {
      state.flows = state.flows.filter((f: Flow) => f.id !== id);
    })),

  renameFlow: (id, name) =>
    set(produce((state: DiagramStore) => {
      const flow = state.flows.find((f: Flow) => f.id === id);
      if (flow) flow.name = name;
    })),

  updateFlow: (id, nodeIds, edgeIds) =>
    set(produce((state: DiagramStore) => {
      const flow = state.flows.find((f: Flow) => f.id === id);
      if (flow) { flow.nodeIds = nodeIds; flow.edgeIds = edgeIds; }
    })),


  addLayer: (name, color) =>
    set(produce((state: DiagramStore) => {
      state.layers.push({ id: generateId('layer'), name, color, visible: true, dimmed: false });
    })),

  removeLayer: (id) =>
    set(produce((state: DiagramStore) => {
      if (id === 'default') return;
      state.layers = state.layers.filter(l => l.id !== id);
      state.nodes.forEach(n => { if (n.layerId === id) n.layerId = undefined; });
      state.participants.forEach(p => { if (p.layerId === id) p.layerId = undefined; });
      if (state.activeLayerId === id) state.activeLayerId = state.layers[0].id;
    })),

  updateLayer: (id, updates) =>
    set(produce((state: DiagramStore) => {
      const layer = state.layers.find(l => l.id === id);
      if (layer) Object.assign(layer, updates);
    })),

  setNodeLayer: (nodeId, layerId) =>
    set(produce((state: DiagramStore) => {
      // Update in active graph
      const graph = state.graphs[state.activeGraphId];
      if (graph) {
        const graphNode = graph.nodes.find((n: Node) => n.id === nodeId);
        if (graphNode) graphNode.layerId = layerId;
        // Force re-render by creating new array reference
        state.nodes = [...graph.nodes];
      }
      // Also update in flat state (for nodes not in active graph)
      const flatNode = state.nodes.find((n: Node) => n.id === nodeId);
      if (flatNode) flatNode.layerId = layerId;
    })),

  setParticipantLayer: (participantId, layerId) =>
    set(produce((state: DiagramStore) => {
      const p = state.participants.find(pp => pp.id === participantId);
      if (p) p.layerId = layerId;
    })),

  setActiveLayer: (id) =>
    set(produce((state: DiagramStore) => {
      state.activeLayerId = id;
    })),

  updateNodeMeta: (nodeId, meta) =>
    set(produce((state: DiagramStore) => {
      const graph = state.graphs[state.activeGraphId];
      const node = graph?.nodes.find((n: Node) => n.id === nodeId) ?? state.nodes.find(n => n.id === nodeId);
      if (node) {
        node.meta = { ...(node.meta ?? {}), ...meta };
        if (graph) state.nodes = graph.nodes;
        enrollNode(state, node);
        if (node.catalogId) {
          const entry = state.catalog.find(item => item.id === node.catalogId);
          if (entry) {
            mergeIntoCatalog(entry, {
              description: node.meta?.description,
              owner: node.meta?.owner,
              status: node.meta?.status,
              links: node.meta?.links,
            });
            syncCatalogAcrossDocument(state, node.catalogId);
          }
        }
      }
    })),

  groupNodes: (nodeIds, label) =>
    set(produce((state: DiagramStore) => {
      if (nodeIds.length < 2) return;
      const groupId = generateId('group');
      const nodeIdSet = new Set(nodeIds);

      const targetNodes = state.nodes.filter((n: Node) => nodeIds.includes(n.id));
      if (targetNodes.length === 0) return;

      const parentGroupIds = new Set(targetNodes.map((n: Node) => n.parentGroupId).filter(Boolean));
      const commonParent = parentGroupIds.size === 1 ? [...parentGroupIds][0] : undefined;

      const PADDING = 30;
      let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
      targetNodes.forEach((n: Node) => {
        minX = Math.min(minX, n.position.x);
        minY = Math.min(minY, n.position.y);
        maxX = Math.max(maxX, n.position.x + n.size.width);
        maxY = Math.max(maxY, n.position.y + n.size.height);
      });

      const subGroupsToNest: string[] = [];
      state.groups.forEach((g: NodeGroup) => {
        if (g.childNodeIds.length > 0 && g.childNodeIds.every((cid: string) => nodeIds.includes(cid))) {
          subGroupsToNest.push(g.id);
          minX = Math.min(minX, g.position.x);
          minY = Math.min(minY, g.position.y);
          maxX = Math.max(maxX, g.position.x + g.size.width);
          maxY = Math.max(maxY, g.position.y + g.size.height);
        }
      });

      // ── Scan for crossing edges ──────────────────────────────────────────
      // extToInt: edges where source is OUTSIDE, target is INSIDE → Group Input
      // intToExt: edges where source is INSIDE, target is OUTSIDE → Group Output
      //
      // Exclude edges where either endpoint is a boundary node (group_input /
      // group_output). These are internal infrastructure of the parent group
      // and must not be re-routed when creating a nested group inside it.
      const isBoundaryNode = (nodeId: string) => {
        const n = state.nodes.find((nd: Node) => nd.id === nodeId);
        return n?.type === 'group_input' || n?.type === 'group_output';
      };

      const extToInt: Edge[] = [];
      const intToExt: Edge[] = [];
      state.edges.forEach((e: Edge) => {
        if (isBoundaryNode(e.sourceNodeId) || isBoundaryNode(e.targetNodeId)) return;
        const srcIn = nodeIdSet.has(e.sourceNodeId);
        const tgtIn = nodeIdSet.has(e.targetNodeId);
        if (!srcIn && tgtIn) extToInt.push(e);
        if (srcIn && !tgtIn) intToExt.push(e);
      });

      const BOUNDARY_W = 140;
      const groupCenterY = (minY + maxY) / 2;

      // ── Build GroupInput node ──────────────────────────────────────────────
      // Per signal: input socket (gi-in-N at x:0) receives from outside,
      //             output socket (gi-out-N at x:BOUNDARY_W) feeds into group.
      const giId = generateId('gi');
      const giSocketMap = new Map<string, { internalNodeId: string; internalSocketId: string; label: string }>();
      extToInt.forEach((e: Edge) => {
        const key = `${e.targetNodeId}:${e.targetSocketId}`;
        if (!giSocketMap.has(key)) {
          const internalNode = state.nodes.find((n: Node) => n.id === e.targetNodeId);
          const sock = internalNode?.sockets.find((s: SocketDefinition) => s.id === e.targetSocketId);
          giSocketMap.set(key, {
            internalNodeId: e.targetNodeId,
            internalSocketId: e.targetSocketId,
            label: sock?.label ?? `In ${giSocketMap.size + 1}`,
          });
        }
      });
      const giEntries = [...giSocketMap.entries()];
      const giSockets: SocketDefinition[] = giEntries.flatMap(([, entry], i) => [
        {
          id: `gi-in-${i}`,
          type: 'input' as const,
          dataType: 'data',
          label: entry.label,
          position: { x: 0, y: socketY(i) },
          order: i,
        },
        {
          id: `gi-out-${i}`,
          type: 'output' as const,
          dataType: 'data',
          label: entry.label,
          position: { x: BOUNDARY_W, y: socketY(i) },
          order: i,
        },
      ]);
      const giNode: Node = {
        id: giId,
        type: 'group_input',
        label: 'Group Input',
        position: { x: minX - BOUNDARY_W - 60, y: groupCenterY - calcNodeHeight(giEntries.length, giEntries.length) / 2 },
        size: { width: BOUNDARY_W, height: calcNodeHeight(giEntries.length, giEntries.length) },
        sockets: giSockets,
        parentGroupId: groupId,
      };

      // ── Build GroupOutput node (input sockets, one per unique internal source socket) ──
      const goId = generateId('go');
      const goSocketMap = new Map<string, { internalNodeId: string; internalSocketId: string; label: string }>();
      intToExt.forEach((e: Edge) => {
        const key = `${e.sourceNodeId}:${e.sourceSocketId}`;
        if (!goSocketMap.has(key)) {
          const internalNode = state.nodes.find((n: Node) => n.id === e.sourceNodeId);
          const sock = internalNode?.sockets.find((s: SocketDefinition) => s.id === e.sourceSocketId);
          goSocketMap.set(key, {
            internalNodeId: e.sourceNodeId,
            internalSocketId: e.sourceSocketId,
            label: sock?.label ?? `Out ${goSocketMap.size + 1}`,
          });
        }
      });
      const goEntries = [...goSocketMap.entries()];
      const goSockets: SocketDefinition[] = goEntries.map(([, entry], i) => ({
        id: `go-in-${i}`,
        type: 'input' as const,
        dataType: 'data',
        label: entry.label,
        position: { x: 0, y: socketY(i) },
        order: i,
      }));
      const goNode: Node = {
        id: goId,
        type: 'group_output',
        label: 'Group Output',
        position: { x: maxX + 60, y: groupCenterY - calcNodeHeight(Math.max(goSockets.length, 1), 0) / 2 },
        size: { width: BOUNDARY_W, height: calcNodeHeight(Math.max(goSockets.length, 1), 0) },
        sockets: goSockets,
        parentGroupId: groupId,
      };

      // ── Rewrite crossing edges through boundary nodes ────────────────────
      // Each crossing edge becomes 2 edges: external segment + internal segment.
      const edgeIdsToRemove = new Set<string>();

      extToInt.forEach((e: Edge) => {
        const key = `${e.targetNodeId}:${e.targetSocketId}`;
        const entryIdx = giEntries.findIndex(([k]) => k === key);
        if (entryIdx === -1) return;
        const giSocketId = `gi-out-${entryIdx}`;
        edgeIdsToRemove.add(e.id);
        // External segment: original source → GroupInput INPUT socket (left side, x:0)
        state.edges.push({
          id: generateId('edge'),
          sourceNodeId: e.sourceNodeId,
          sourceSocketId: e.sourceSocketId,
          targetNodeId: giId,
          targetSocketId: `gi-in-${entryIdx}`,
          label: e.label,
          animated: e.animated,
        });
        // Internal segment: GroupInput OUTPUT socket (right side, x:BOUNDARY_W) → original target
        state.edges.push({
          id: generateId('edge'),
          sourceNodeId: giId,
          sourceSocketId: giSocketId,
          targetNodeId: e.targetNodeId,
          targetSocketId: e.targetSocketId,
        });
      });

      intToExt.forEach((e: Edge) => {
        const key = `${e.sourceNodeId}:${e.sourceSocketId}`;
        const entryIdx = goEntries.findIndex(([k]) => k === key);
        if (entryIdx === -1) return;
        const goSocketId = `go-in-${entryIdx}`;
        edgeIdsToRemove.add(e.id);
        // Internal segment: original source → GroupOutput (input socket)
        state.edges.push({
          id: generateId('edge'),
          sourceNodeId: e.sourceNodeId,
          sourceSocketId: e.sourceSocketId,
          targetNodeId: goId,
          targetSocketId: goSocketId,
        });
        // External segment: GroupOutput (input socket) → original target
        // GroupOutput exposes output sockets mirroring its inputs for external connections
        const goOutSocketId = `go-out-${entryIdx}`;
        // Ensure the mirror output socket exists on goNode
        if (!goNode.sockets.find((s: SocketDefinition) => s.id === goOutSocketId)) {
          goNode.sockets.push({
            id: goOutSocketId,
            type: 'output' as const,
            dataType: 'data',
            label: goSockets[entryIdx]?.label ?? `Out ${entryIdx + 1}`,
            position: { x: BOUNDARY_W, y: socketY(entryIdx) },
            order: entryIdx,
          });
        }
        state.edges.push({
          id: generateId('edge'),
          sourceNodeId: goId,
          sourceSocketId: goOutSocketId,
          targetNodeId: e.targetNodeId,
          targetSocketId: e.targetSocketId,
          label: e.label,
          animated: e.animated,
        });
      });

      // Remove original crossing edges
      state.edges = state.edges.filter((e: Edge) => !edgeIdsToRemove.has(e.id));

      // ── Add boundary nodes to state ──────────────────────────────────────
      if (giSockets.length > 0) state.nodes.push(giNode);
      if (goSockets.length > 0) state.nodes.push(goNode);

      // ── Build the group ──────────────────────────────────────────────────
      const directChildNodeIds = nodeIds.filter(nid => !subGroupsToNest.some(sgid => {
        const sg = state.groups.find((g: NodeGroup) => g.id === sgid);
        return sg?.childNodeIds.includes(nid);
      }));
      // Include boundary nodes in childNodeIds
      if (giSockets.length > 0) directChildNodeIds.push(giId);
      if (goSockets.length > 0) directChildNodeIds.push(goId);

      const newGroup: NodeGroup = {
        id: groupId,
        label: label ?? `Group ${state.groups.length + 1}`,
        color: '#64748b',
        childNodeIds: directChildNodeIds,
        childGroupIds: [...subGroupsToNest],
        parentGroupId: commonParent,
        collapsed: false,
        position: { x: minX - PADDING, y: minY - PADDING - 24 },
        size: { width: maxX - minX + PADDING * 2, height: maxY - minY + PADDING * 2 + 24 },
        groupInputNodeId: giSockets.length > 0 ? giId : undefined,
        groupOutputNodeId: goSockets.length > 0 ? goId : undefined,
      };

      state.groups.push(newGroup);

      // Set parentGroupId on all direct children
      directChildNodeIds.forEach((nid: string) => {
        const n = state.nodes.find((node: Node) => node.id === nid);
        if (n) n.parentGroupId = groupId;
      });

      subGroupsToNest.forEach((sgid: string) => {
        const sg = state.groups.find((g: NodeGroup) => g.id === sgid);
        if (sg) sg.parentGroupId = groupId;
      });

      if (commonParent) {
        const parent = state.groups.find((g: NodeGroup) => g.id === commonParent);
        if (parent) {
          parent.childNodeIds = parent.childNodeIds.filter((cid: string) => !nodeIds.includes(cid));
          parent.childGroupIds = parent.childGroupIds.filter((cgid: string) => !subGroupsToNest.includes(cgid));
          if (!parent.childGroupIds.includes(groupId)) {
            parent.childGroupIds.push(groupId);
          }
        }
      }
    })),

  createFrame: (position, size, nodeIds, groupIds = []) => {
    const groupId = generateId('group');
    const FRAME_COLORS = ['#f59e0b', '#6366f1', '#10b981', '#ec4899', '#06b6d4', '#ef4444', '#8b5cf6', '#f97316', '#84cc16'];
    const randomFrameColor = FRAME_COLORS[Math.floor(Math.random() * FRAME_COLORS.length)];
    set(produce((state: DiagramStore) => {
      const newGroup: NodeGroup = {
        id: groupId,
        label: `Frame ${state.groups.filter((g: NodeGroup) => g.isFrame).length + 1}`,
        color: randomFrameColor,
        childNodeIds: nodeIds,
        childGroupIds: groupIds,
        collapsed: false,
        position,
        size,
        isFrame: true,
      };
      state.groups.push(newGroup);
      nodeIds.forEach((nid: string) => {
        const n = state.nodes.find((node: Node) => node.id === nid);
        if (n) n.parentGroupId = groupId;
      });
      groupIds.forEach((gid: string) => {
        const g = state.groups.find((grp: NodeGroup) => grp.id === gid);
        if (g) g.parentGroupId = groupId;
      });
    }));
    return groupId;
  },

  ungroupNodes: (groupId) =>
    set(produce((state: DiagramStore) => {
      const group = state.groups.find((g: NodeGroup) => g.id === groupId);
      if (!group) return;

      const parentId = group.parentGroupId;
      const giId = group.groupInputNodeId;
      const goId = group.groupOutputNodeId;

      // ── Restore crossing edges ───────────────────────────────────────────
      // The grouping split each crossing edge into:
      //   External: externalNode[out] → GroupInput[gi-in-N]
      //   Internal: GroupInput[gi-out-N] → internalNode[in]
      // and:
      //   Internal: internalNode[out] → GroupOutput[go-in-N]
      //   External: GroupOutput[go-out-N] → externalNode[in]
      //
      // On ungroup we reconstruct: externalNode[out] → internalNode[in]
      // by joining the external segment's source to the internal segment's target.

      const edgeIdsToRemove = new Set<string>();

      if (giId) {
        // Find all external segments that target GroupInput (gi-in-N)
        const externalToGi = state.edges.filter((e: Edge) => e.targetNodeId === giId);
        externalToGi.forEach((extEdge: Edge) => {
          // Derive the paired output socket index from gi-in-N → gi-out-N
          const inSocketId = extEdge.targetSocketId; // e.g. "gi-in-0"
          const idx = inSocketId.replace('gi-in-', '');
          const outSocketId = `gi-out-${idx}`;
          // Find the internal segment sourcing from that gi-out socket
          const intEdge = state.edges.find((e: Edge) => e.sourceNodeId === giId && e.sourceSocketId === outSocketId);
          if (intEdge) {
            // Reconstruct the original edge: external source → internal target
            state.edges.push({
              id: generateId('edge'),
              sourceNodeId: extEdge.sourceNodeId,
              sourceSocketId: extEdge.sourceSocketId,
              targetNodeId: intEdge.targetNodeId,
              targetSocketId: intEdge.targetSocketId,
              label: extEdge.label,
              animated: extEdge.animated,
            });
            edgeIdsToRemove.add(intEdge.id);
          }
          edgeIdsToRemove.add(extEdge.id);
        });
      }

      if (goId) {
        // Find all internal segments that target GroupOutput (go-in-N)
        const internalToGo = state.edges.filter((e: Edge) => e.targetNodeId === goId);
        internalToGo.forEach((intEdge: Edge) => {
          const inSocketId = intEdge.targetSocketId; // e.g. "go-in-0"
          const idx = inSocketId.replace('go-in-', '');
          const outSocketId = `go-out-${idx}`;
          // Find the external segment sourcing from go-out
          const extEdge = state.edges.find((e: Edge) => e.sourceNodeId === goId && e.sourceSocketId === outSocketId);
          if (extEdge) {
            state.edges.push({
              id: generateId('edge'),
              sourceNodeId: intEdge.sourceNodeId,
              sourceSocketId: intEdge.sourceSocketId,
              targetNodeId: extEdge.targetNodeId,
              targetSocketId: extEdge.targetSocketId,
              label: extEdge.label,
              animated: extEdge.animated,
            });
            edgeIdsToRemove.add(extEdge.id);
          }
          edgeIdsToRemove.add(intEdge.id);
        });
      }

      // Remove all split edge segments
      state.edges = state.edges.filter((e: Edge) => !edgeIdsToRemove.has(e.id));

      // ── Remove boundary nodes ─────────────────────────────────────────────
      const boundaryIds = new Set([giId, goId].filter(Boolean) as string[]);
      state.nodes = state.nodes.filter((n: Node) => !boundaryIds.has(n.id));

      // ── Release real child nodes back to parent (or root) ────────────────
      const realChildIds = group.childNodeIds.filter(nid => !boundaryIds.has(nid));
      realChildIds.forEach((nid: string) => {
        const n = state.nodes.find((node: Node) => node.id === nid);
        if (n) n.parentGroupId = parentId;
      });

      // Move child sub-groups back to parent
      group.childGroupIds.forEach((cgid: string) => {
        const sg = state.groups.find((g: NodeGroup) => g.id === cgid);
        if (sg) sg.parentGroupId = parentId;
      });

      // Update parent's child lists if nested
      if (parentId) {
        const parent = state.groups.find((g: NodeGroup) => g.id === parentId);
        if (parent) {
          parent.childGroupIds = parent.childGroupIds.filter((cgid: string) => cgid !== groupId);
          parent.childNodeIds.push(...realChildIds);
          parent.childGroupIds.push(...group.childGroupIds);
        }
      }

      // Remove the group and exit nav if inside it
      state.groups = state.groups.filter((g: NodeGroup) => g.id !== groupId);
      state.groupNavStack = state.groupNavStack.filter(entry => entry.groupId !== groupId);
    })),

  toggleGroupCollapse: (groupId) =>
    set(produce((state: DiagramStore) => {
      const group = state.groups.find((g: NodeGroup) => g.id === groupId);
      if (group) group.collapsed = !group.collapsed;
    })),

  updateGroup: (groupId, updates) =>
    set(produce((state: DiagramStore) => {
      const group = state.groups.find((g: NodeGroup) => g.id === groupId);
      if (group) Object.assign(group, updates);
    })),

  recalcGroupBounds: (groupId) =>
    set(produce((state: DiagramStore) => {
      _recalcBounds(state, groupId);
      // Also recalc all ancestor groups
      const ancestors = _getAncestorGroupIds(state.groups, groupId);
      ancestors.forEach(aid => _recalcBounds(state, aid));
    })),

  addNodeToGroup: (nodeId, groupId) =>
    set(produce((state: DiagramStore) => {
      const group = state.groups.find((g: NodeGroup) => g.id === groupId);
      const node = state.nodes.find((n: Node) => n.id === nodeId);
      if (!group || !node) return;
      if (!group.childNodeIds.includes(nodeId)) group.childNodeIds.push(nodeId);
      node.parentGroupId = groupId;
    })),

  removeNodeFromGroup: (nodeId, groupId) =>
    set(produce((state: DiagramStore) => {
      const group = state.groups.find((g: NodeGroup) => g.id === groupId);
      const node = state.nodes.find((n: Node) => n.id === nodeId);
      if (!group || !node) return;
      group.childNodeIds = group.childNodeIds.filter((id: string) => id !== nodeId);
      if (node.parentGroupId === groupId) node.parentGroupId = undefined;
    })),

  addGroupToFrame: (childGroupId, frameId) =>
    set(produce((state: DiagramStore) => {
      const frame = state.groups.find((g: NodeGroup) => g.id === frameId);
      const child = state.groups.find((g: NodeGroup) => g.id === childGroupId);
      if (!frame || !child) return;
      if (!frame.childGroupIds.includes(childGroupId)) frame.childGroupIds.push(childGroupId);
      child.parentGroupId = frameId;
    })),

  removeGroupFromFrame: (childGroupId, frameId) =>
    set(produce((state: DiagramStore) => {
      const frame = state.groups.find((g: NodeGroup) => g.id === frameId);
      const child = state.groups.find((g: NodeGroup) => g.id === childGroupId);
      if (!frame || !child) return;
      frame.childGroupIds = frame.childGroupIds.filter((id: string) => id !== childGroupId);
      if (child.parentGroupId === frameId) child.parentGroupId = undefined;
    })),

  // Group navigation (Blender-style enter/exit)
  // This is a PURE VIEW FILTER — no page switching, no data copying.
  // The full graph (nodes/edges) stays intact. App.tsx uses groupNavStack
  // to decide which nodes/edges/groups to render.
  enterGroup: (groupId) =>
    set(produce((state: DiagramStore) => {
      const group = state.groups.find((g: NodeGroup) => g.id === groupId);
      if (!group || group.isFrame) return;
      // Just push onto the nav stack — viewport saved for restore on exit
      state.groupNavStack.push({
        groupId,
        groupLabel: group.label,
        returnViewport: { ...state.viewport },
      });
      state.selectedItems = [];
    })),

  exitGroup: () =>
    set(produce((state: DiagramStore) => {
      if (state.groupNavStack.length === 0) return;
      const entry = state.groupNavStack.pop()!;
      // Restore viewport to where we were before entering
      state.viewport = { ...entry.returnViewport };
      state.selectedItems = [];
    })),

  // ── Subgraph navigation actions (delegated to graphSlice) ───────────────
  ...createGraphActions<DiagramStore>((mutator) =>
    set(produce((state: DiagramStore) => mutator(state))),
  ),

  getActiveGraph: () => {
    const state = get();
    return state.graphs[state.activeGraphId];
  },

  // ── Frame actions (visual-only grouping) ─────────────────────────────────
  updateFrame: (frameId, updates) =>
    set(produce((state: DiagramStore) => {
      const frame = state.frames.find((f: NodeGroup) => f.id === frameId);
      if (frame) Object.assign(frame, updates);
    })),

  deleteFrame: (frameId) =>
    set(produce((state: DiagramStore) => {
      state.frames = state.frames.filter((f: NodeGroup) => f.id !== frameId);
    })),

  // ── Multi-page actions (delegated to pageSlice) ─────────────────────────
  ...createPageActions<DiagramStore>((mutator) =>
    set(produce((state: DiagramStore) => mutator(state))),
  ),

  ...createCatalogActions<DiagramStore>((mutator) =>
    set(produce((state: DiagramStore) => mutator(state))),
  ),

  getActivePage: () => {
    const state = get();
    return state.pages.find(p => p.id === state.activePage);
  },

  // Connection
  startConnection: (nodeId, socketId) =>
    set(produce((state: DiagramStore) => {
      const node = state.nodes.find((n: Node) => n.id === nodeId);
      if (!node) return;

      const socket = node.sockets.find((s: SocketDefinition) => s.id === socketId);
      if (socket) {
        state.connectionState.isConnecting = true;
        state.connectionState.sourceNodeId = nodeId;
        state.connectionState.sourceSocketId = socketId;
        state.connectionState.tempEdge = {
          startX: node.position.x + socket.position.x,
          startY: node.position.y + socket.position.y,
          endX: node.position.x + socket.position.x,
          endY: node.position.y + socket.position.y,
        };
      }
    })),

  updateTempEdge: (endX, endY) =>
    set(produce((state: DiagramStore) => {
      if (state.connectionState.tempEdge) {
        state.connectionState.tempEdge.endX = endX;
        state.connectionState.tempEdge.endY = endY;
      }
    })),

  completeConnection: (targetNodeId, targetSocketId) => {
    let newEdge: Edge | undefined;
    set(produce((state: DiagramStore) => {
      const { sourceNodeId, sourceSocketId } = state.connectionState;
      if (sourceNodeId && sourceSocketId) {

        const sn = state.nodes.find((n: Node) => n.id === sourceNodeId);
        const tn = state.nodes.find((n: Node) => n.id === targetNodeId);
        const freeform = !!(sn && tn && connectionUsesFreeformSockets(sn.type, tn.type));
        const normalized = sn && tn
          ? normalizeOutputToInput(sn, sourceSocketId, tn, targetSocketId, { freeform })
          : null;

        const canConnectState = !!normalized && !state.edges.some(e =>
          e.sourceNodeId === normalized.sourceNodeId && e.sourceSocketId === normalized.sourceSocketId &&
          e.targetNodeId === normalized.targetNodeId && e.targetSocketId === normalized.targetSocketId
        );

        if (canConnectState && normalized) {
          const srcNode = state.nodes.find((n: Node) => n.id === normalized.sourceNodeId);
          const tgtNode = state.nodes.find((n: Node) => n.id === normalized.targetNodeId);
          if (srcNode && tgtNode) {
            applyMagneticDragPolarity(srcNode, normalized.sourceSocketId, tgtNode, normalized.targetSocketId);
          }
          const edgeId = generateId('edge');
          const edge: Edge = {
            id: edgeId,
            sourceNodeId: normalized.sourceNodeId,
            sourceSocketId: normalized.sourceSocketId,
            targetNodeId: normalized.targetNodeId,
            targetSocketId: normalized.targetSocketId,
          };
          const kit = getKit(state.pages.find(p => p.id === state.activePage)?.kit);
          if (kit.defaultEdge.edgeType !== 'arrow') edge.edgeType = kit.defaultEdge.edgeType;
          if (kit.defaultEdge.dashStyle) edge.dashStyle = kit.defaultEdge.dashStyle;
          // Add to active graph
          const graph = state.graphs[state.activeGraphId];
          if (graph) {
            graph.edges.push(edge);
            state.edges = graph.edges;
          } else {
            state.edges.push(edge);
          }
          newEdge = edge;
        }
      }
      state.connectionState = { ...emptyConnectionState };
    }));
    return newEdge;
  },

  cancelConnection: () =>
    set(produce((state: DiagramStore) => { state.connectionState = { ...emptyConnectionState }; })),

  getConnectedSockets: () => {
    const state = get();
    const s = new Set<string>();
    state.edges.forEach(e => {
      s.add(`${e.sourceNodeId}-${e.sourceSocketId}`);
      s.add(`${e.targetNodeId}-${e.targetSocketId}`);
    });
    return s;
  },

  canConnect: (sourceNodeId, sourceSocketId, targetNodeId, targetSocketId) => {
    const state = get();
    if (sourceNodeId === targetNodeId) return false;
    const sn = state.nodes.find(n => n.id === sourceNodeId);
    const tn = state.nodes.find(n => n.id === targetNodeId);
    const ss = sn?.sockets.find(s => s.id === sourceSocketId);
    const ts = tn?.sockets.find(s => s.id === targetSocketId);
    if (!ss || !ts || !sn || !tn) return false;
    const freeform = connectionUsesFreeformSockets(sn.type, tn.type);
    const normalized = normalizeOutputToInput(sn, sourceSocketId, tn, targetSocketId, { freeform });
    if (!normalized) return false;
    return !state.edges.some(e =>
      e.sourceNodeId === normalized.sourceNodeId && e.sourceSocketId === normalized.sourceSocketId &&
      e.targetNodeId === normalized.targetNodeId && e.targetSocketId === normalized.targetSocketId
    );
  },

  getGroupDescendantNodeIds: (groupId) => {
    const state = get();
    return _getDescendantNodeIds(state.groups, groupId);
  },

  // ── Sequence diagram actions (delegated to sequenceSlice) ───────────────
  ...createSequenceActions<DiagramStore>((mutator) =>
    set(produce((state: DiagramStore) => mutator(state))),
  ),
}));

const PERSIST_DEBOUNCE_MS = 300;
let persistTimer: ReturnType<typeof setTimeout> | null = null;

function persistedSliceUnchanged(state: DiagramStore, prev: DiagramStore): boolean {
  return (
    state.nodes === prev.nodes &&
    state.edges === prev.edges &&
    state.viewport === prev.viewport &&
    state.layers === prev.layers &&
    state.activeLayerId === prev.activeLayerId &&
    state.groups === prev.groups &&
    state.diagramType === prev.diagramType &&
    state.participants === prev.participants &&
    state.messages === prev.messages &&
    state.fragments === prev.fragments &&
    state.participantGroups === prev.participantGroups &&
    state.seqLifelineLength === prev.seqLifelineLength &&
    state.pages === prev.pages &&
    state.activePage === prev.activePage &&
    state.flows === prev.flows &&
    state.catalog === prev.catalog &&
    state.graphs === prev.graphs &&
    state.activeGraphId === prev.activeGraphId &&
    state.graphNavStack === prev.graphNavStack
  );
}

// Persist document slices only (skip selection / connection rubber-band), and
// debounce so pan, zoom, and multi-node layout do not stringify the whole
// diagram on every mouse move.
function flushPersistedDiagram() {
  if (!persistTimer) return;
  clearTimeout(persistTimer);
  persistTimer = null;
  savePersistedDiagram(useDiagramStore.getState());
}

useDiagramStore.subscribe((state, prev) => {
  if (persistedSliceUnchanged(state, prev)) return;
  if (persistTimer) clearTimeout(persistTimer);
  persistTimer = setTimeout(() => {
    persistTimer = null;
    savePersistedDiagram(useDiagramStore.getState());
  }, PERSIST_DEBOUNCE_MS);
});

if (typeof window !== 'undefined') {
  window.addEventListener('pagehide', flushPersistedDiagram);
}
